// src/lib/useDemoPage.js
// Instrumentation des pages de démonstration : demo_view à l'affichage, demo_cta
// via la fonction retournée, demo_complete quand le visiteur est allé au bout, et noindex
// optionnel pour les démos de marque.
//
// demo_complete (une seule fois par affichage de page) : le visiteur a lu la page jusqu'en bas
// (au moins 85 % de la hauteur, ou page tenant dans l'écran) ET y est resté au moins 20 secondes.
// Les deux conditions évitent de compter un défilement rapide ou un onglet laissé ouvert.
import { useCallback, useEffect } from "react";
import { track } from "@/lib/track";

const COMPLETE_SCROLL_RATIO = 0.85;
const COMPLETE_MIN_SECONDS = 20;

function baseMeta(scenario) {
  const p = new URLSearchParams(window.location.search);
  return {
    route: window.location.pathname,
    scenario,
    source: p.get("utm_source") || document.referrer || null,
  };
}

function scrolledToEnd() {
  const doc = document.documentElement;
  const total = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0);
  const viewport = window.innerHeight || doc.clientHeight || 0;
  if (total <= viewport * 1.1) return true; // page courte : tout est déjà visible
  const seen = (window.pageYOffset || doc.scrollTop || 0) + viewport;
  return seen / total >= COMPLETE_SCROLL_RATIO;
}

export function useDemoPage(scenario, { noindex = false } = {}) {
  useEffect(() => {
    let tag = null;
    if (noindex) {
      tag = document.createElement("meta");
      tag.name = "robots";
      tag.content = "noindex, nofollow";
      document.head.appendChild(tag);
    }
    track("demo_view", { meta: baseMeta(scenario) });

    // demo_complete : lecture jusqu'en bas + durée minimale sur la page.
    const startedAt = Date.now();
    let reachedEnd = scrolledToEnd();
    let waited = false;
    let done = false;
    let timer = null;

    const maybeComplete = () => {
      if (done || !reachedEnd || !waited || document.hidden) return;
      done = true;
      track("demo_complete", {
        meta: { ...baseMeta(scenario), seconds: Math.round((Date.now() - startedAt) / 1000) },
      });
    };
    const onScroll = () => {
      if (!reachedEnd && scrolledToEnd()) reachedEnd = true;
      maybeComplete();
    };
    timer = window.setTimeout(() => {
      waited = true;
      onScroll();
    }, COMPLETE_MIN_SECONDS * 1000);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", maybeComplete);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", maybeComplete);
      if (tag && tag.parentNode) tag.parentNode.removeChild(tag);
    };
  }, [scenario, noindex]);

  return useCallback(
    (cta) => track("demo_cta", { meta: { ...baseMeta(scenario), cta } }),
    [scenario]
  );
}
